import { useState } from 'react';
import { toast } from 'sonner';
import { Trophy, Medal, ArrowRight, Loader2 } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../ui/button';

const money = (n) => Number(n || 0);

/**
 * iter313 — who's winning the morning. Americano/mixer rank individuals on points
 * scored (+ win bonus); a fixed-partner round robin ranks the pairs.
 */
export default function StandingsCard({ standings, nm, sessionId, canScore, onAdvanced }) {
  const [busy, setBusy] = useState(false);
  if (!standings || !standings.rows?.length) return null;
  const { kind, rows, format, win_bonus: bonus, rated, complete, pending } = standings;
  const label = {
    americano: 'Americano', pairs: 'Round robin', mixer: 'Open shuffle',
    mexicano: 'Mexicano', king: 'King of the Court',
    knockout: 'Knockout', groups_knockout: 'Groups + knockout',
  }[format] || 'Standings';
  const leader = rows[0];
  const champ = standings.champion_names;

  const advance = async () => {
    setBusy(true);
    try {
      await api.post(`/sessions/${sessionId}/next-round`);
      toast.success('Next round is up');
      onAdvanced?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not build the next round');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border p-3 space-y-2.5" data-testid="standings-card">
      {champ && (
        <div className="rounded-xl bg-amber-500/15 border border-amber-500/40 px-3 py-2.5 text-center" data-testid="bracket-champion">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-amber-700 dark:text-amber-300">Champions</p>
          <p className="font-display text-lg font-black">{champ.map((n) => n.split(' ')[0]).join(' & ')}</p>
        </div>
      )}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-display text-xs font-bold uppercase tracking-[0.16em] inline-flex items-center gap-1.5">
            <Trophy className="w-3.5 h-3.5 text-amber-500" /> {label}
            {standings.round_name && !champ ? ` · ${standings.round_name}` : ' standings'}
          </p>
          <p className="text-xs text-muted-foreground">
            {kind === 'pairs' ? 'Ranked on matches won, then point difference' : 'Ranked on points scored'}
            {bonus ? ` · +${bonus} per win` : ''}
            {rated === false ? ' · not counted for ratings' : ''}
          </p>
        </div>
        {complete && !standings.progressive && (
          <span className="shrink-0 inline-flex items-center gap-1 h-6 px-2 rounded-full bg-amber-500/15 border border-amber-500/40 text-xs font-bold text-amber-700 dark:text-amber-300"
            data-testid="standings-winner-chip">
            <Medal className="w-3 h-3" />
            {kind === 'pairs' ? leader.names.join(' & ') : leader.name} wins
          </span>
        )}
      </div>

      <div className="space-y-1">
        {rows.map((r) => (
          <div key={kind === 'pairs' ? r.pair_index : r.user_id}
            data-testid={`standings-row-${kind === 'pairs' ? r.pair_index : r.user_id}`}
            className={`flex items-center gap-2 h-9 px-2.5 rounded-xl text-xs font-semibold ${
              r.rank === 1 ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-muted/40'
            }`}>
            <span className="w-4 text-center font-display font-black tabular-nums text-muted-foreground">{r.rank}</span>
            <span className="flex-1 truncate">
              {kind === 'pairs' ? r.names.map((n) => n.split(' ')[0]).join(' & ') : nm(r.user_id)}
            </span>
            <span className="text-muted-foreground tabular-nums shrink-0">
              {r.wins}W · {r.losses}L
            </span>
            <span className="font-display font-black tabular-nums w-12 text-right shrink-0">
              {kind === 'pairs' ? `${money(r.diff) > 0 ? '+' : ''}${money(r.diff)}` : money(r.total)}
            </span>
          </div>
        ))}
      </div>

      {pending > 0 && !standings.progressive && (
        <p className="text-xs text-muted-foreground">
          {pending} match{pending > 1 ? 'es' : ''} still to play.
        </p>
      )}

      {standings.progressive && (
        <div className="space-y-1.5">
          {!!standings.queue?.length && (
            <p className="text-xs text-muted-foreground" data-testid="king-queue">
              Waiting to come on: {standings.queue.map(nm).join(', ')}
            </p>
          )}
          {canScore && !champ && (
            <Button size="sm" className="rounded-full w-full h-9 text-xs" disabled={busy || !standings.can_next_round}
              onClick={advance} data-testid="next-round-btn">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (
                <>{standings.stage === 'groups' ? 'Start the knockout' : 'Build the next round'} <ArrowRight className="w-3.5 h-3.5 ml-1.5" /></>
              )}
            </Button>
          )}
          {!standings.can_next_round && !champ && (
            <p className="text-xs text-muted-foreground text-center">
              Log every court in this round to unlock the next one.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
